/**
 * Regras puras do CCO › Mapa de Comando — Nexora Grid (US-4402).
 *
 * Criado em: 01/11/2026 · Última modificação: 02/11/2026 · Versão: 1.1.0
 *
 * Sem three.js e sem React, para testar com `node --test`:
 * - estado do equipamento na maquete (normal, atenção, alarme, sem dados) a partir da nota de anomalia do CCO;
 * - modelo 3D de cada tipo de equipamento (biblioteca da US-4401);
 * - medida principal da placa de telemetria e faixas de pressão (NBR 12218) e de nível;
 * - posição dos setores em volta do CCO e dos equipamentos na plataforma;
 * - rota de um evento de pessoa, agente, regra ou campo (âncoras que a cena resolve);
 * - pessoa uniformizada pelo papel.
 */
import type { Nivel } from "./anomalia";
import type { TipoEquip } from "./dados";
import type { Ator } from "./observabilidade";

/** Mesma faixa de `NBR_12218` em `medidas.ts` (aqui sem importar valores, para o `node --test` ler o .ts direto). */
const NBR_12218 = { minima: 10, maxima: 40 } as const;

export type EstadoMapa = "ok" | "atencao" | "alarme" | "offline";
export type ModeloMapa = "poco" | "reservatorio" | "elevado" | "pressao" | "macro" | "vrp" | "motobomba";
export type ModeloPessoa = "tecnico" | "gestor" | "operador" | "gerente";
export type TipoAtor = "humano" | "agente" | "regra" | "ronda" | "sistema" | "campo";

/** Estado na maquete: dado velho ou sem comunicação = sem dados; crítico = alarme; atenção e alto = atenção. */
export function estadoDoNivel(nivel: Nivel, velho: boolean): EstadoMapa {
  if (velho || nivel === "sem_comunicacao") return "offline";
  if (nivel === "critico") return "alarme";
  if (nivel === "atencao" || nivel === "alto") return "atencao";
  return "ok";
}

/** Pior estado de uma lista (setor): alarme > atenção > sem dados > normal. */
export function piorEstado(estados: EstadoMapa[]): EstadoMapa {
  const ordem: EstadoMapa[] = ["ok", "offline", "atencao", "alarme"];
  return estados.reduce<EstadoMapa>((pior, e) => (ordem.indexOf(e) > ordem.indexOf(pior) ? e : pior), "ok");
}

/** Modelo 3D pelo tipo do CCO e, quando há, pelo tipo do cadastro (poço profundo, reservatório elevado). */
export function modeloDoTipo(tipo: TipoEquip, tipoCadastro?: string, identificacao?: string): ModeloMapa {
  if (tipo === "reservatorio") return /elevado|taca/.test(tipoCadastro ?? "") || /(^|[^A-Za-z])REL[-_ ]?\d/i.test(identificacao ?? "") ? "elevado" : "reservatorio";
  if (tipo === "poco" || tipoCadastro === "poco_profundo" || tipoCadastro === "poco") return "poco";
  if (tipo === "bomba") return "motobomba";
  if (tipo === "vrp") return "vrp";
  if (tipo === "macro") return "macro";
  return "pressao";
}

/** Tipos do cadastro que viram equipamento na maquete (registro, ventosa e hidrante ficam de fora: são centenas e não têm modelo). */
const TIPO_DO_CADASTRO: Record<string, TipoEquip> = { reservatorio: "reservatorio", poco_profundo: "poco", bomba: "bomba", elevatoria: "bomba",
  macromedidor: "macro", vrp: "vrp", ponto_pressao: "pressao" };

/** Tipo do equipamento da maquete a partir do tipo do cadastro; `null` se o tipo não entra na maquete. */
export const tipoDoCadastro = (tipo: string): TipoEquip | null => TIPO_DO_CADASTRO[tipo] ?? null;

/** Código do setor para juntar o cadastro ("RAP-001") com a telemetria ("rap-001"); sem setor = `sem-setor`. */
export const codigoDoSetor = (setor: string | null | undefined): string => (setor ?? "").trim().toLowerCase() || "sem-setor";

/** Título do setor na maquete. */
export const tituloDoSetor = (codigo: string): string => (codigo === "sem-setor" ? "SEM SETOR" : codigo.toUpperCase());

export const MEDIDAS_PRINCIPAIS: Record<TipoEquip, string[]> = {
  reservatorio: ["nivel_reservatorio_pct", "nivel_m"],
  pressao: ["pressao_rede_mca", "pressao_mca", "pressao_jusante_mca"],
  vrp: ["pressao_jusante_mca", "pressao_montante_mca"],
  macro: ["vazao_instantanea_m3h", "vazao_saida_m3h", "vazao_entrada_m3h"],
  poco: ["vazao_instantanea_m3h", "pressao_recalque_mca", "nivel_dinamico_m", "corrente_fase_a"],
  bomba: ["pressao_recalque_mca", "vazao_instantanea_m3h", "corrente_fase_a", "potencia_ativa_kw"],
  outro: [],
};

/** Medida da placa: a primeira da lista do tipo que o equipamento mede; senão, a primeira em ordem alfabética. */
export function medidaPrincipal(tipo: TipoEquip, medidas: Iterable<string>): string | null {
  const lista = [...medidas];
  return MEDIDAS_PRINCIPAIS[tipo].find((m) => lista.includes(m)) ?? [...lista].sort()[0] ?? null;
}

/** Pressão da rede pela NBR 12218 (10–40 mca). */
export function classePressao(v: number | null | undefined): "ok" | "baixa" | "alta" | "sd" {
  if (v === null || v === undefined || Number.isNaN(v)) return "sd";
  if (v < NBR_12218.minima) return "baixa";
  if (v > NBR_12218.maxima) return "alta";
  return "ok";
}

/** Nível do reservatório: crítico abaixo de 15%, baixo abaixo de 30%. */
export function classeNivel(pct: number | null | undefined): "ok" | "baixo" | "critico" | "sd" {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return "sd";
  if (pct < 15) return "critico";
  if (pct < 30) return "baixo";
  return "ok";
}

/** Profundidade da plataforma de um setor e folga entre plataformas. */
const PROFUNDIDADE_SETOR = 7.2, FOLGA_SETOR = 5, RAIO_INICIAL = 21.5;

/**
 * Setores em anéis concêntricos em volta do CCO: quantos couberem no anel (pela largura da maior plataforma) e, se
 * sobrarem, o anel seguinte, um pouco defasado. Com poucos setores é o anel único de sempre.
 * `giro` deixa o lado comprido da plataforma tangente ao anel; `raio` é o do anel mais externo.
 */
export function disporSetores(larguras: number[]): { posicoes: { x: number; z: number; giro: number }[]; raio: number } {
  const posicoes: { x: number; z: number; giro: number }[] = [];
  const maior = Math.max(...larguras, 11.5);
  let raio = RAIO_INICIAL, externo = RAIO_INICIAL, anel = 0;
  while (posicoes.length < larguras.length) {
    const cabem = Math.max(1, Math.floor((2 * Math.PI * raio) / (maior + FOLGA_SETOR)));
    const neste = Math.min(cabem, larguras.length - posicoes.length);
    for (let k = 0; k < neste; k++) {
      const a = (k / neste) * Math.PI * 2 - Math.PI / 2 + Math.PI / neste + (anel % 2 ? Math.PI / (2 * cabem) : 0);
      posicoes.push({ x: Math.cos(a) * raio, z: Math.sin(a) * raio, giro: -(a + Math.PI / 2) });
    }
    externo = raio;
    raio += PROFUNDIDADE_SETOR + FOLGA_SETOR + 2;
    anel++;
  }
  return { posicoes, raio: externo };
}

/** Acima disto, o setor passa a duas fileiras de cada lado da adutora (reservatórios não se sobrepõem). */
export const MAX_NA_FILA = 6;

/** Largura da plataforma do setor (cabe a fila de equipamentos ao longo da adutora). */
export const larguraSetor = (n: number) => Math.max(11.5, (n > MAX_NA_FILA ? Math.ceil(n / 2) * 3.8 : n * 3.0) + 2.6);

/** Equipamentos ao longo da plataforma, alternando os lados da adutora; com mais de 6, em duas fileiras afastadas. */
export function posicoesNoSetor(n: number): { x: number; z: number }[] {
  const meia = larguraSetor(n) / 2 - 1.9;
  if (n > MAX_NA_FILA) {
    const porLado = Math.ceil(n / 2);
    return Array.from({ length: n }, (_, j) => {
      const lado = j % 2 ? 1 : -1, i = Math.floor(j / 2);
      return { x: porLado === 1 ? 0 : -meia + (i * 2 * meia) / (porLado - 1), z: lado * 2.3 };
    });
  }
  return Array.from({ length: n }, (_, j) => ({ x: n === 1 ? 0 : -meia + (j * 2 * meia) / (n - 1), z: (j % 2 ? 1 : -1) * 0.9 }));
}

/** Equipe de agentes de IA (ADR-0016): o Gestor coordena e é o único que fala com o usuário; os especialistas só leem. */
export type MembroEquipe = { id: string; nome: string; curto: string; papel: string; disponivel: boolean; motivo?: string };
export const EQUIPE_DE_AGENTES: MembroEquipe[] = [
  { id: "gestor", nome: "Gestor", curto: "Gestor", papel: "Coordena a equipe e é o único que fala com o usuário", disponivel: true },
  { id: "analista_telemetria", nome: "Analista de Telemetria", curto: "Analista", papel: "Séries, perfis por dia da semana, comparações, desvios e saúde dos sensores", disponivel: true },
  { id: "energia", nome: "Energia", curto: "Energia", papel: "Consumo de energia (kWh), custo e eficiência (kWh/m³)", disponivel: true },
  { id: "alarmes_incidentes", nome: "Alarmes e Incidentes", curto: "Alarmes", papel: "O que alarmou, onde, quantas vezes e desde quando", disponivel: true },
  { id: "ativos_campo", nome: "Ativos e Campo", curto: "Ativos", papel: "Cadastro dos equipamentos e saúde dos sensores para a manutenção", disponivel: true },
  { id: "hidraulico", nome: "Hidráulico (gêmeo digital)", curto: "Hidráulico", papel: "Previsto × medido e cenários EPANET", disponivel: false,
    motivo: "aguarda as ferramentas do gêmeo digital (GDT-2801)" },
  { id: "sentinela", nome: "Sentinela", curto: "Sentinela", papel: "Cibersegurança: vigia tentativas de injeção de comandos e vínculos novos", disponivel: true },
  { id: "setor", nome: "Agente de setor", curto: "Setor", papel: "Responde por um setor quando o gestor delega", disponivel: true },
];
export type AgenteDoMapa = MembroEquipe & { eventos: number; ultimo: string | null; canais: string[] };

/** A equipe inteira, com a atividade (eventos em 24 h) de quem já agiu; agente novo que aparecer nos eventos também entra. */
export function equipeDoMapa(atores: Ator[]): AgenteDoMapa[] {
  const porId = new Map(atores.filter((a) => a.tipo_ator === "agente").map((a) => [a.ator, a]));
  const membros: AgenteDoMapa[] = EQUIPE_DE_AGENTES.map((m) => {
    const a = porId.get(m.id);
    porId.delete(m.id);
    return { ...m, eventos: a?.eventos ?? 0, ultimo: a?.ultimo ?? null, canais: a?.canais ?? [] };
  });
  porId.forEach((a) => membros.push({ id: a.ator, nome: a.ator, curto: a.ator, papel: a.papel || "agente de IA", disponivel: true,
    eventos: a.eventos, ultimo: a.ultimo, canais: a.canais }));
  return membros;
}

/** Agente ou pessoa "em ação" se agiu na janela (padrão: 2 min). */
export function emAcao(ultimo: string | null | undefined, agora: number, janelaMs = 120000): boolean {
  if (!ultimo) return false;
  const t = Date.parse(ultimo);
  return !Number.isNaN(t) && agora - t <= janelaMs;
}

/** Próxima ronda do gestor: janelas de 15 min com 5 min de espera para os agregados fecharem (RONDA_ATRASO_S). */
export function proximaRonda(agora: number): number {
  const janela = 15 * 60000, atraso = 5 * 60000;
  return Math.floor((agora - atraso) / janela) * janela + janela + atraso;
}

/** O que o agente está fazendo, em palavras de quem opera: "Analisando", "Em espera" (de plantão) ou "Indisponível". */
export function situacaoDoAgente(a: { id: string; disponivel: boolean; ultimo: string | null; motivo?: string }, agora: number,
                                 hora: (iso: string | null) => string): { rotulo: string; ativo: boolean; detalhe: string } {
  if (!a.disponivel) return { rotulo: "Indisponível", ativo: false, detalhe: a.motivo ?? "" };
  if (emAcao(a.ultimo, agora)) return { rotulo: "Analisando", ativo: true, detalhe: "" };
  const ultima = a.ultimo ? `última atividade ${hora(a.ultimo)}` : "sem atividade nas últimas 24 h";
  if (a.id === "gestor") {
    const min = Math.max(1, Math.ceil((proximaRonda(agora) - agora) / 60000));
    return { rotulo: "Em espera", ativo: false, detalhe: `${ultima} · próxima ronda em ${min} min` };
  }
  return { rotulo: "Em espera", ativo: false, detalhe: `${ultima} · age quando o gestor chamar` };
}

/** Uniforme pela função: técnico de campo, operador do CCO, gerente/supervisor e engenharia/gestão. */
export function modeloDaPessoa(papel: string): ModeloPessoa {
  const p = papel.toLowerCase();
  if (/tecnic|campo|manuten/.test(p)) return "tecnico";
  if (/gerent|supervis|administr|diret/.test(p)) return "gerente";
  if (/engenh|gestor|coorden/.test(p)) return "gestor";
  return "operador";
}

/** Canal de mensagem (a pessoa aparece em campo, ao lado do equipamento). */
export const canalDeMensagem = (canal: string) => /whats|telegram/i.test(canal);

/**
 * Âncoras da rota de um evento, na ordem do pulso de luz:
 * - `g` gestor · `a:<ator>` agente · `p:<ator>` pessoa no CCO · `f:<ator>:<device>` pessoa em campo · `d:<device>` equipamento.
 * Pessoa por mensagem com equipamento aparece em campo; regra, ronda, sistema e campo partem do equipamento.
 */
export function rotaDoEvento(e: { ator: string; tipo_ator: TipoAtor; canal: string; device_ids: string[] }, existe: (device: string) => boolean): string[] {
  const devices = e.device_ids.filter(existe).slice(0, 2).map((d) => `d:${d}`);
  if (e.tipo_ator === "agente") return e.ator === "gestor" ? ["g", ...devices] : [`a:${e.ator}`, "g", ...devices];
  if (e.tipo_ator === "humano") {
    const primeiro = e.device_ids.find(existe);
    const origem = canalDeMensagem(e.canal) && primeiro ? `f:${e.ator}:${primeiro}` : `p:${e.ator}`;
    return [origem, "g", ...devices];
  }
  return devices.length ? [devices[0], "g"] : [];
}

/** Cor do pulso pelo tipo de quem agiu (mesma família da CCO › Local). */
export const COR_EVENTO: Record<TipoAtor, string> = {
  humano: "#25d366", agente: "#6fb6d8", regra: "#f0b13a", ronda: "#56e0d0", sistema: "#8aa4b8", campo: "#ff5348",
};
export const COR_TELEGRAM = "#3a9be0", COR_SEGURANCA = "#a996e0";

/** Cor do evento como no protótipo: pessoa pelo canal (WhatsApp verde, Telegram azul) e a Sentinela na cor da segurança. */
export function corDoEvento(e: { ator: string; tipo_ator: TipoAtor; canal: string }): string {
  if (e.tipo_ator === "humano" && /telegram/i.test(e.canal)) return COR_TELEGRAM;
  if (e.tipo_ator === "agente" && e.ator === "sentinela") return COR_SEGURANCA;
  return COR_EVENTO[e.tipo_ator];
}

/** Atividade ao vivo da equipe: os eventos que chegam pelo fluxo atualizam o último e a contagem sem esperar a recarga dos atores. */
export function comAtividade(equipe: AgenteDoMapa[], eventos: { ator: string; tipo_ator: TipoAtor; instante: string | null }[]): AgenteDoMapa[] {
  const porAtor = new Map<string, { n: number; ultimo: string | null }>();
  eventos.forEach((e) => {
    if (e.tipo_ator !== "agente") return;
    const x = porAtor.get(e.ator) ?? { n: 0, ultimo: null };
    x.n++;
    if (e.instante && (!x.ultimo || e.instante > x.ultimo)) x.ultimo = e.instante;
    porAtor.set(e.ator, x);
  });
  return equipe.map((m) => {
    const x = porAtor.get(m.id);
    if (!x) return m;
    return { ...m, eventos: Math.max(m.eventos, x.n), ultimo: !m.ultimo || (x.ultimo && x.ultimo > m.ultimo) ? x.ultimo : m.ultimo };
  });
}
